import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { DatetimePicker, Picker, Popup } from 'react-vant';
import DataDetails from 'components/data-details';
import HostDetails from 'components/host-details';
import { useAtom } from 'jotai';
import {
  isMasterAtom,
  guildIdAtom,
  uidAtom,
  selectUserDataAtom,
  dateTypeAtom,
  countdownAtom,
} from 'store';
import { MasterSalary, MemberSalary } from 'api/type';
import { getArgFromUrl } from 'utils';
import { masterSalary, memberSalary, guildData } from 'api';
import { _T } from '@dana/components';
import './data.css';
import Nav from 'components/nav';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isoWeek);
dayjs.extend(utc);

// 使用 GMT+3 时区计算半月周期
const gmt3Now = dayjs().utcOffset(180);
const currentDay = gmt3Now.date();
const currentMonth = gmt3Now.month(); // 0-11, 1 代表二月
// 二月份特殊处理：上半月1-14，下半月15-月末；其他月份：上半月1-15，下半月16-月末
const isFebruary = currentMonth === 1;
const halfMonthSplit = isFebruary ? 14 : 15;
const isFirstHalf = currentDay <= halfMonthSplit;

// 计算当前周期的起止日期
const currentPeriodStart = isFirstHalf
  ? gmt3Now.startOf('month')
  : gmt3Now.date(halfMonthSplit + 1);

// 格式化日期显示
const formatPeriodDate = (d: dayjs.Dayjs) => d.format('YYYY.M.D');

// 当前周期值
const currentPeriodValue = currentPeriodStart.format('YYYYMMDD');

// 构建半月选项列表
const generateHalfMonthColumns = () => {
  // 数据起始日期：2026年2月1日
  const dataStartDate = dayjs('2026-02-01').utcOffset(180);
  // 最早可选日期：2025年1月1日
  const earliestDate = dayjs('2025-01-01').utcOffset(180);

  const columns: { text: string; displayText: string; value: string }[] = [];

  // 确定截止日期：如果当前周期 < 数据起始日期，截止到2025年1月1日；否则截止到数据起始日期
  const endDate = currentPeriodStart.isBefore(dataStartDate) ? earliestDate : dataStartDate;

  // 从当前周期开始，往前生成到截止日期的所有半月选项
  let current = currentPeriodStart.clone();
  let isFirst = true;

  while (current.isAfter(endDate) || current.isSame(endDate, 'day')) {
    const day = current.date();
    const month = current.month(); // 0-11, 1 代表二月
    // 二月份特殊处理：上半月1-14，下半月15-月末；其他月份：上半月1-15，下半月16-月末
    const isFeb = month === 1;
    const splitDay = isFeb ? 14 : 15;
    const isCurrentFirstHalf = day <= splitDay;

    const periodStart = isCurrentFirstHalf
      ? current.startOf('month')
      : current.date(splitDay + 1);
    const periodEndDate = isCurrentFirstHalf
      ? current.date(splitDay)
      : current.endOf('month');

    const displayText = isFirst
      ? 'This Period'
      : `${periodStart.format('M.D')}~${periodEndDate.format('M.D')}`;

    columns.push({
      text: `${isFirst ? 'This Period' : displayText}(${formatPeriodDate(periodStart)}~${formatPeriodDate(periodEndDate)})`,
      displayText,
      value: periodStart.format('YYYYMMDD'),
    });

    isFirst = false;

    // 移动到上一个周期
    if (isCurrentFirstHalf) {
      // 当前是上半月，上一个周期是上个月的下半月
      const prevMonth = current.subtract(1, 'month');
      const isPrevFeb = prevMonth.month() === 1;
      const prevSplitDay = isPrevFeb ? 14 : 15;
      current = prevMonth.date(prevSplitDay + 1);
    } else {
      // 当前是下半月，上一个周期是本月的上半月
      current = current.startOf('month');
    }
  }

  return columns;
};

const halfMonthColumns = generateHalfMonthColumns();

export default function Data() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const [isMaster] = useAtom(isMasterAtom);
  const [guildId] = useAtom(guildIdAtom);
  const [masterData, setMasterData] = useState<MasterSalary>();
  const [memberData, setMemberData] = useState<MemberSalary>();
  const [list, setList] = useState([]);
  const [uid] = useAtom(uidAtom);
  const [userInfo, setUserInfo] = useAtom(selectUserDataAtom);

  const [dateType] = useAtom(dateTypeAtom);

  const [, setCountdownAtom] = useAtom(countdownAtom);

  const [dateValue, setDateValue] = useState(new Date());
  const [halfMonthValue, setHalfMonthValue] = useState(currentPeriodValue);
  const [tempHalfMonthValue, setTempHalfMonthValue] = useState(halfMonthValue);
  const value = useMemo(() => {
    if (dateType === 'month') {
      return dateValue.getFullYear() + String(dateValue.getMonth() + 1).padStart(2, '0');
    }
    return halfMonthValue;
  }, [dateType, dateValue, halfMonthValue]);

  const isCurrentDate = useMemo(() => {
    if (dateType === 'month') {
      const date = new Date();
      const currentDate = date.getFullYear() + String(date.getMonth() + 1).padStart(2, '0');
      return currentDate === value;
    }
    return value === currentPeriodValue;
  }, [dateType, value]);

  const displayDateText = useMemo(() => {
    if (dateType === 'month') {
      return dateValue.getFullYear() + '/' + (dateValue.getMonth() + 1);
    }
    return halfMonthColumns.find((item) => item.value === halfMonthValue)?.displayText;
  }, [dateType, dateValue, halfMonthColumns, halfMonthValue]);

  const fetchData = async () => {
    if (isMaster) {
      // const res1 = await masterSalary(guildId, value, dateType === 'HALF_MONTH')
      // setMasterData(() => res1)
      // fetchGuildData()
      // const res2 = await memberSalary(guildId, value, uid)
      // setMemberData(() => res2)
    } else {
      const res = await memberSalary(guildId, value, uid, dateType === 'HALF_MONTH');
      setMemberData(() => res);
    }
  };

  const fetchGuildData = async () => {
    const res = await guildData(guildId, value, uid, dateType === 'HALF_MONTH');
    const data = res.data;
    if (isMaster) {
      setList(data.list.sort((a: any, b: any) => b.giftFlowTotal - a.giftFlowTotal));
    }
    setMasterData(() => data.main);
    setMemberData(() => data.salary);
    setUserInfo({
      uid: Number(getArgFromUrl('uid')) || 0,
      totalTime: data.salary?.masterLiveTime?.inVoiceTotal,
      day: data.salary?.masterLiveTime?.activeDayTotal,
      date: value,
      avatar: data.avatar,
      nickName: data.nickname,
      danaId: data.danaId,
    });

    setCountdownAtom(data?.countDown || 0);
  };

  useEffect(() => {
    // fetchData()
    fetchGuildData();
  }, [value]);

  const changeBtnText = () => {
    // nut-picker-cancel-btn
    // nut-picker-confirm-btn
    const element1 = document.querySelectorAll('.rv-picker__cancel');
    element1.forEach((element: any) => {
      element.dir = 'auto';
    });

    const element2 = document.querySelectorAll('.rv-picker__confirm');
    element2.forEach((element: any) => {
      element.dir = 'auto';
    });

    const element3 = document.querySelectorAll('.rv-picker__columns');
    element3.forEach((element) => {
      const d = document.createElement('div');
      d.style.backgroundColor = '#F5F7F7';
      d.style.width = '375px';
      d.style.height = '44px';
      d.style.position = 'absolute';
      d.style.left = '0px';
      d.style.top = '50%';
      d.style.transform = 'translateY(-50%)';
      element.append(d);
    });
  };

  const goBack = () => {
    navigate(-1);
  };

  const showDate = () => {
    setShow(true);
    setTimeout(() => {
      changeBtnText();
    }, 100);
  };

  return (
    <div>
      <Nav title={'数据细节'}></Nav>
      <div
        className={`bg-[#F2F5F5] pt-[12px] overflow-scroll pb-[44px]`}
        style={{
          height: `${window.innerHeight - 45}px`,
        }}
      >
        <div
          className="mx-auto flex justify-between items-center w-[351px] h-[42px] rounded-lg bg-white"
          onClick={showDate}
        >
          <div
            dir="auto"
            className="ml-[12px] text-[#333] font-sans text-[16px] font-bold leading-[18px]"
          >
            {_T('时间')}
          </div>
          <div className="flex items-center justify-center">
            <div
              className="bg-cover w-[18px] h-[18px]"
              style={{
                backgroundImage: `url(${require('asset/imgs/date.png')})`,
              }}
            ></div>
            <div
              dir="auto"
              className="mr-[3px] ml-[6px] text-[#333] font-sans text-[14px] font-bold leading-[18px]"
            >
              {displayDateText}
            </div>
            <div
              className="bg-cover w-[18px] h-[18px] mr-[12px]"
              style={{
                backgroundImage: `url(${require('asset/imgs/bottom.png')})`,
              }}
            ></div>
          </div>
        </div>
        <DataDetails
          detailList={list}
          masterData={masterData}
          memberData={memberData}
          date={value}
          isCurrentDate={isCurrentDate}
          type={isMaster ? 'master' : 'member'}
        ></DataDetails>
        {isMaster && (
          <HostDetails data={list} isCurrentDate={isCurrentDate} date={value}></HostDetails>
        )}
      </div>
      <Popup visible={show} position="bottom" onClose={() => setShow(false)}>
        {dateType === 'month' && (
          <DatetimePicker
            onCancel={() => setShow(false)}
            type="year-month"
            minDate={(() => {
              // 数据起始月份：2026年2月
              const dataStartDate = new Date(2026, 1, 1);
              // 最早可选月份：2025年1月
              const earliestDate = new Date(2025, 0, 1);
              const gmt3Now = dayjs().utcOffset(180); // 180分钟 = GMT+3
              const currentMonth = new Date(gmt3Now.year(), gmt3Now.month(), 1);
              // 如果当前月份 < 数据起始月份，则最小日期为2025年1月；否则为数据起始月份
              return currentMonth < dataStartDate ? earliestDate : dataStartDate;
            })()}
            maxDate={(() => {
              // 使用 GMT+3 时区计算当前月
              const gmt3Now = dayjs().utcOffset(180); // 180分钟 = GMT+3
              return new Date(gmt3Now.year(), gmt3Now.month() + 1, 0); // 当前月最后一天
            })()}
            value={dateValue}
            visible={show}
            confirmButtonText={_T('确定')}
            cancelButtonText={_T('取消')}
            onConfirm={(v: Date) => {
              setDateValue(v);
              setShow(false);
            }}
          />
        )}
        {dateType === 'HALF_MONTH' && (
          <Picker
            onCancel={() => setShow(false)}
            columns={halfMonthColumns}
            placeholder={null}
            visible={show}
            value={tempHalfMonthValue}
            confirmButtonText={_T('确定')}
            cancelButtonText={_T('取消')}
            onChange={(v: string) => {
              setTempHalfMonthValue(v);
            }}
            onConfirm={(v: string) => {
              setHalfMonthValue(v);
              setShow(false);
            }}
          />
        )}
      </Popup>
    </div>
  );
}
